import { Plus } from "lucide-react";
import { useMoneyFormat } from "@/hooks/use-money-format";
import {
  RecordContextProvider,
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import "@/styles/list-skeleton.css";

import { Avatar } from "../contacts/Avatar";
import { findDealLabel } from "../deals/dealUtils";
import { ListSkeleton } from "../misc/ListSkeleton";
import { Status } from "../misc/Status";
import { usePersonName } from "../misc/usePersonName";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { isOverdue } from "../tasks/tasksPredicate";
import type { Company, Contact, Deal, Task } from "../types";

/** Items shown before "Show all" in each section. */
const PREVIEW_COUNT = 5;
const MAX_ITEMS = 100;

interface RelatedSectionProps {
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}

/** A titled group of related records with its count and an add shortcut. */
const RelatedSection = ({
  title,
  count,
  action,
  children,
}: RelatedSectionProps) => (
  <section className="flex flex-col gap-3">
    <div className="flex min-h-9 items-center justify-between gap-2">
      <h3 className="flex items-baseline gap-2 text-lg font-semibold tracking-tight">
        {title}
        {count != null && (
          <span className="text-sm font-medium tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
      </h3>
      {action}
    </div>
    {children}
  </section>
);

const AddLink = ({
  to,
  state,
  label,
}: {
  to: string;
  state?: unknown;
  label: string;
}) => (
  <Link
    to={to}
    state={state}
    aria-label={label}
    title={label}
    className="liquid-glass glass-press flex size-9 shrink-0 items-center justify-center rounded-full text-[var(--glass-accent)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
  >
    <Plus className="size-5" strokeWidth={2.25} />
  </Link>
);

const RelatedCard = ({
  to,
  state,
  children,
}: {
  to: string;
  state?: unknown;
  children: ReactNode;
}) => (
  <li>
    <Link
      to={to}
      state={state}
      className="record-card record-card-press flex items-center gap-3 rounded-3xl px-4 py-3 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {children}
    </Link>
  </li>
);

const EmptyRelated = ({ children }: { children: ReactNode }) => (
  <p className="rounded-3xl border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
    {children}
  </p>
);

/** First PREVIEW_COUNT items, with a toggle to reveal the rest. */
const Preview = <T,>({
  items,
  render,
}: {
  items: T[];
  render: (item: T) => ReactNode;
}) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, PREVIEW_COUNT);
  return (
    <>
      <ul className="flex flex-col gap-2.5">{visible.map(render)}</ul>
      {items.length > PREVIEW_COUNT && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-center rounded-full"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? translate("crm.common.show_less")
            : translate("resources.companies.related.show_all", {
                count: items.length,
              })}
        </Button>
      )}
    </>
  );
};

/** Contacts of the company, fetched once for the contacts and tasks sections. */
export const useCompanyContacts = (companyId: Identifier) =>
  useGetList<Contact>("contacts_summary", {
    filter: { company_id: companyId },
    pagination: { page: 1, perPage: MAX_ITEMS },
    sort: { field: "last_name", order: "ASC" },
  });

export const CompanyContactsSection = ({
  company,
  contacts,
  isPending,
}: {
  company: Company;
  contacts?: Contact[];
  isPending: boolean;
}) => {
  const translate = useTranslate();
  const personName = usePersonName();
  const location = useLocation();
  return (
    <RelatedSection
      title={translate("resources.companies.related.contacts")}
      count={isPending ? undefined : (contacts?.length ?? 0)}
      action={
        <AddLink
          to="/contacts/create"
          state={{ record: { company_id: company.id } }}
          label={translate("resources.companies.related.add_contact")}
        />
      }
    >
      {isPending ? (
        <ListSkeleton variant="cards" rows={2} />
      ) : !contacts?.length ? (
        <EmptyRelated>
          {translate("resources.companies.related.empty_contacts")}
        </EmptyRelated>
      ) : (
        <Preview
          items={contacts}
          render={(contact) => (
            <RecordContextProvider key={contact.id} value={contact}>
              <RelatedCard
                to={`/contacts/${contact.id}/show`}
                state={{ from: location.pathname }}
              >
                <Avatar />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">
                    {personName(contact)}
                  </div>
                  {(!!contact.title || !!contact.nb_tasks) && (
                    <div className="truncate text-sm text-muted-foreground">
                      {[
                        contact.title,
                        contact.nb_tasks
                          ? translate("crm.common.task_count", {
                              smart_count: contact.nb_tasks,
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>
                <Status status={contact.status} />
              </RelatedCard>
            </RecordContextProvider>
          )}
        />
      )}
    </RelatedSection>
  );
};

export const CompanyDealsSection = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();
  const { data: deals, isPending } = useGetList<Deal>("deals", {
    filter: { company_id: company.id },
    pagination: { page: 1, perPage: MAX_ITEMS },
    sort: { field: "updated_at", order: "DESC" },
  });
  const { formatCompactMoney: formatAmount } = useMoneyFormat();
  return (
    <RelatedSection
      title={translate("resources.companies.related.deals")}
      count={isPending ? undefined : (deals?.length ?? 0)}
      action={
        <AddLink
          to="/deals/create"
          state={{ record: { company_id: company.id } }}
          label={translate("resources.companies.related.add_deal")}
        />
      }
    >
      {isPending ? (
        <ListSkeleton variant="cards" rows={2} avatar={false} />
      ) : !deals?.length ? (
        <EmptyRelated>
          {translate("resources.companies.related.empty_deals")}
        </EmptyRelated>
      ) : (
        <Preview
          items={deals}
          render={(deal) => (
            <RelatedCard key={deal.id} to={`/deals/${deal.id}/show`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{deal.name}</div>
                <div
                  className={cn(
                    "truncate text-sm text-muted-foreground",
                    deal.archived_at && "italic",
                  )}
                >
                  {findDealLabel(dealStages, deal.stage) ?? deal.stage}
                </div>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                {formatAmount(deal.amount)}
              </span>
            </RelatedCard>
          )}
        />
      )}
    </RelatedSection>
  );
};

/**
 * Pending tasks of the company. Tasks only reference a contact
 * (tasks.contact_id), so these are the open tasks of the company's contacts.
 */
export const CompanyTasksSection = ({
  contacts,
  isContactsPending,
}: {
  contacts?: Contact[];
  isContactsPending: boolean;
}) => {
  const translate = useTranslate();
  const personName = usePersonName();
  const [locale = "es"] = useLocaleState();
  const contactIds = (contacts ?? []).map((contact) => contact.id);
  const hasContacts = contactIds.length > 0;
  const { data: tasks, isPending: isTasksPending } = useGetList<Task>(
    "tasks",
    {
      filter: {
        "contact_id@in": `(${contactIds.join(",")})`,
        "done_date@is": null,
      },
      pagination: { page: 1, perPage: MAX_ITEMS },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: hasContacts },
  );
  const isPending = isContactsPending || (hasContacts && isTasksPending);
  const visibleTasks = hasContacts ? (tasks ?? []) : [];
  const contactById = new Map(
    (contacts ?? []).map((contact) => [String(contact.id), contact]),
  );
  const formatDue = (date: string) =>
    new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(
      new Date(date),
    );
  return (
    <RelatedSection
      title={translate("resources.companies.related.tasks")}
      count={isPending ? undefined : visibleTasks.length}
    >
      {isPending ? (
        <ListSkeleton variant="cards" rows={2} avatar={false} />
      ) : !visibleTasks.length ? (
        <EmptyRelated>
          {translate("resources.companies.related.empty_tasks")}
        </EmptyRelated>
      ) : (
        <Preview
          items={visibleTasks}
          render={(task) => {
            const contact = contactById.get(String(task.contact_id));
            return (
              <RelatedCard
                key={task.id}
                to={`/contacts/${task.contact_id}/show?tab=tasks`}
              >
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 font-medium">{task.text}</div>
                  {contact && (
                    <div className="truncate text-sm text-muted-foreground">
                      {personName(contact)}
                    </div>
                  )}
                </div>
                {task.due_date && (
                  <span
                    className={cn(
                      "shrink-0 text-xs font-medium tabular-nums text-muted-foreground",
                      isOverdue(task.due_date) && "text-destructive",
                    )}
                  >
                    {formatDue(task.due_date)}
                  </span>
                )}
              </RelatedCard>
            );
          }}
        />
      )}
    </RelatedSection>
  );
};

/** Contacts, deals and tasks of a company, stacked (mobile company page). */
export const CompanyRelatedSections = ({ company }: { company: Company }) => {
  const { data: contacts, isPending } = useCompanyContacts(company.id);
  return (
    <div className="flex flex-col gap-7">
      <CompanyContactsSection
        company={company}
        contacts={contacts}
        isPending={isPending}
      />
      <CompanyDealsSection company={company} />
      <CompanyTasksSection contacts={contacts} isContactsPending={isPending} />
    </div>
  );
};
